/**
 * Buttons in an HTML frame page may ask the app to act, declaratively:
 * `<button data-denker-action="open_connect" data-toolkit="gmail">`. The page
 * names an action from a short allow-list; the app decides, scoped to the
 * frame's own project, and never answers back into the page.
 *
 * Everything here reads untrusted page data: unknown names, malformed or
 * oversized arguments drop the whole action.
 */

export type HtmlFrameHostAction =
  | { name: "open_connect"; args: { toolkit: string } }
  | { name: "open_frame"; args: { frameId: string } }
  | { name: "open_task"; args: { taskId: string } }
  | { name: "answer_question"; args: { questionId: string; optionId: string } }
  | { name: "run_task"; args: { taskId: string } };

/**
 * `overlay`: the app's own click asked the page what lies under the pointer;
 * the click is real, the page's answer is not proof of the user's choice.
 * `interactive`: the page reported a click after the user entered it; page
 * script could have sent that report itself.
 */
export type HtmlFrameActionMode = "overlay" | "interactive";

/** The frame the page lives in; its project scopes every action. */
export interface HtmlFrameActionHost {
  frameId: string;
  projectId: string;
}

const ARG_PATTERNS = {
  uuid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  token: /^[\w:.@-]+$/,
};

type ArgKind = keyof typeof ARG_PATTERNS;

/** Argument names are the `dataset` keys of the page's `data-*` attributes. */
const ACTION_ARGS: Record<
  HtmlFrameHostAction["name"],
  Record<string, ArgKind>
> = {
  open_connect: { toolkit: "token" },
  open_frame: { frameId: "uuid" },
  open_task: { taskId: "uuid" },
  answer_question: { questionId: "uuid", optionId: "token" },
  run_task: { taskId: "uuid" },
};

const MAX_ARG_KEYS = 8;
const MAX_ARG_LENGTH = 128;

function isActionName(name: unknown): name is HtmlFrameHostAction["name"] {
  return (
    typeof name === "string" &&
    Object.prototype.hasOwnProperty.call(ACTION_ARGS, name)
  );
}

function readArg(value: unknown, kind: ArgKind): string | null {
  if (typeof value !== "string" || value.length > MAX_ARG_LENGTH) return null;
  return ARG_PATTERNS[kind].test(value) ? value : null;
}

/**
 * The allow-listed action in `raw` (`{ name, args }`), with only its own
 * arguments; other `data-*` attributes on the element are ignored.
 */
export function readHtmlFrameHostAction(
  raw: unknown,
): HtmlFrameHostAction | null {
  if (!raw || typeof raw !== "object") return null;
  const { name, args } = raw as { name?: unknown; args?: unknown };
  if (!isActionName(name)) return null;
  if (!args || typeof args !== "object" || Array.isArray(args)) return null;
  if (Object.keys(args).length > MAX_ARG_KEYS) return null;
  const read: Record<string, string> = {};
  for (const [key, kind] of Object.entries(ACTION_ARGS[name])) {
    const value = readArg((args as Record<string, unknown>)[key], kind);
    if (value === null) return null;
    read[key] = value;
  }
  return { name, args: read } as HtmlFrameHostAction;
}

/**
 * Actions that write or wake an agent. Even on the app's own click, the page
 * decided which action lies under the pointer (its script can move or swap
 * elements), so the user's choice is proven only by the app's confirmation.
 */
const STATE_CHANGING: ReadonlySet<HtmlFrameHostAction["name"]> = new Set([
  "answer_question",
  "run_task",
]);

/**
 * A state-changing action always asks, in the app's own words (a Run asks
 * "Run now?"). Anything the page reported itself asks too, since no user
 * gesture is proven. Only opening a page, task or Connections on the
 * app's own click goes straight through: it changes nothing.
 */
export function needsConfirmation(
  action: HtmlFrameHostAction,
  mode: HtmlFrameActionMode,
): boolean {
  return mode === "interactive" || STATE_CHANGING.has(action.name);
}
